import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["onSubmit"]
const _hoisted_2 = { class: "form__submit btn btn--fullscreen" }

import { computed, onBeforeMount, ref, watch } from "vue";
import FormTooltip from "@/components/forms/FormTooltip.vue";
import FormInput from "@/components/forms/FormInput.vue";
import FormRow from "@/components/forms/FormRow.vue";
import FormErrorsList from "@/components/forms/FormErrorsList.vue";
import Fieldset from "@/components/forms/Fieldset.vue";
import MembershipForm from "@/components/memberships/MembershipForm.vue";
import Legend from "@/components/layout/Legend.vue";
import { useMembershipService } from "@/inversify.config";

import { useI18n } from "vue3-i18n";
import { notifyError, notifySuccess } from "@/notify";
import { SucceededOrNotResponse } from "@/types/responses/succeededOrNotResponse";
import {Plan, PlanType} from "@/types/entities/plan";


export default /*#__PURE__*/_defineComponent({
  __name: 'MembershipsForm',
  setup(__props) {

const { t } = useI18n()
const membershipService = useMembershipService()

const memberships = ref<Plan[]>([])
const taxName = ref()
const coOwnerPlans = computed(() => memberships.value.filter(x => x.type == PlanType.CoOwner))
const corporatePlans = computed(() => memberships.value.filter(x => x.type == PlanType.Corporate))
const syndicatePlans = computed(() => memberships.value.filter(x => x.type == PlanType.Syndicate).sort((a, b) => (a.unitsMin ?? 0) - (b.unitsMin ?? 0)))
const groupedByChapterSyndicatePlans = computed(() => {
  return syndicatePlans.value.reduce((result: any, obj: Plan) => {
    const chapter = obj.chapter ?? "";
    if (!result[chapter]) {
      result[chapter] = [];
    }
    result[chapter].push(obj);
    return result;
  }, {});
})

onBeforeMount(async () => {
  memberships.value = await getMemberships();
  taxName.value = memberships.value.length ? memberships.value[0].product?.accountingSoftwareProduct?.taxName : ""
})

async function getMemberships() {
  return await membershipService.getMemberships() as Plan[]
}

async function updateMemberships(value: Plan[]) {
  return await membershipService.updateMemberships(value)
}

watch(taxName, (newVal) => {
  memberships.value.forEach(x=>{
    if(x.product?.accountingSoftwareProduct != null)
      x.product.accountingSoftwareProduct.taxName = newVal
  })
})

function updateMembership(value: Plan) {
  var membership = memberships.value.find(x => x.id == value.id)
  if (membership != null) membership = value

  let accountcode = membership?.product?.accountingSoftwareProduct?.accountCode
  updateAccountCodes(accountcode?.value ?? "", accountcode?.id ?? "")

  let itemCode = membership?.product?.accountingSoftwareProduct?.itemCode
  updateItemCodes(itemCode?.value ?? "", itemCode?.id ?? "")
}

async function updateAccountCodes(value: string, id:string) {
  memberships.value =  memberships.value.map(x=> {
    let accountCode = x.product?.accountingSoftwareProduct?.accountCode;
    if(accountCode?.id == id) {
      accountCode.value = value;
    }
    return x;
  })
}

async function updateItemCodes(value: string, id:string) {
  memberships.value =  memberships.value.map(x=> {
    let itemCode = x.product?.accountingSoftwareProduct?.itemCode;
    if(itemCode?.id == id) itemCode.value = value;
    return x;
  })
}

const errorsMessages = ref<string[]>([])
function displayErrors(response: SucceededOrNotResponse) {
  response = new SucceededOrNotResponse(response.succeeded, response.errors)
  if (response?.succeeded != undefined && !response?.succeeded) {
    errorsMessages.value = response.getErrorMessages('validation.membership')
  }
  else errorsMessages.value = []
}

async function handleSubmit() {
  const valid = handleValidation()
  if (!valid) {
    notifyError(t('global.formErrorNotification'))
    return
  }

  errorsMessages.value = []
  let response = await updateMemberships(memberships.value)

  if ((response as SucceededOrNotResponse)?.succeeded != undefined && !(response as SucceededOrNotResponse)?.succeeded) {
    notifyError(t('global.formErrorNotification'))
    displayErrors(response as SucceededOrNotResponse)
  }
  else {
    notifySuccess(t('memberships.form.successMessage'))
    memberships.value = response as Plan[]
  }

}

function handleValidation() {
  let valid = memberships.value.length > 0 && memberships.value.every((x: Plan) => {
    return (x.product?.accountingSoftwareProduct?.accountCode?.value?.length ?? 0 > 0) &&
      (x.product?.accountingSoftwareProduct?.itemCode?.value?.length ?? 0 > 0) &&
      ((x.product?.price ?? 0) >= 0) &&
      (x.product?.accountingSoftwareProduct?.taxName?.length ?? 0 > 0)
  })
  return valid 
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", {
    class: "form",
    novalidate: "",
    onSubmit: _withModifiers(handleSubmit, ["prevent"])
  }, [
    (errorsMessages.value.length)
      ? (_openBlock(), _createBlock(FormErrorsList, {
          key: 0,
          title: _unref(t)('global.formErrorNotification'),
          errorsList: errorsMessages.value
        }, null, 8, ["title", "errorsList"]))
      : _createCommentVNode("", true),
    _createVNode(Fieldset, {
      title: _unref(t)(`memberships.form.unitPriceCorpo`)
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(corporatePlans.value, (membership) => {
          return (_openBlock(), _createBlock(MembershipForm, {
            key: membership.id,
            value: membership,
            onPlanUpdated: updateMembership
          }, null, 8, ["value"]))
        }), 128))
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(Fieldset, {
      title: _unref(t)(`memberships.form.unitPriceCoOwner`)
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(coOwnerPlans.value, (membership) => {
          return (_openBlock(), _createBlock(MembershipForm, {
            key: membership.id,
            value: membership,
            onPlanUpdated: updateMembership
          }, null, 8, ["value"]))
        }), 128))
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(Legend, {
      title: _unref(t)(`memberships.form.unitPriceRegular`)
    }, null, 8, ["title"]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedByChapterSyndicatePlans.value, (plans, chapter) => {
      return (_openBlock(), _createBlock(Fieldset, {
        key: chapter,
        title: chapter
      }, {
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(plans, (plan, index) => {
            return (_openBlock(), _createBlock(MembershipForm, {
              key: `${chapter}-${index}`,
              value: plan,
              onPlanUpdated: updateMembership
            }, null, 8, ["value"]))
          }), 128))
        ]),
        _: 2
      }, 1032, ["title"]))
    }), 128)),
    _createVNode(Fieldset, {
      title: _unref(t)(`memberships.tax`)
    }, {
      default: _withCtx(() => [
        _createVNode(FormRow, { "with-three-columns": "" }, {
          default: _withCtx(() => [
            _createVNode(FormInput, {
              ref: `taxNameEl`,
              name: `taxName`,
              type: "text",
              label: _unref(t)(`memberships.form.taxName`),
              modelValue: taxName.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((taxName).value = $event))
            }, null, 8, ["label", "modelValue"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(FormTooltip, null, {
      default: _withCtx(() => [
        _createElementVNode("p", null, [
          _createElementVNode("strong", null, _toDisplayString(_unref(t)('memberships.form.invalidTaxTypeMessage')), 1)
        ])
      ]),
      _: 1
    }),
    _createElementVNode("button", _hoisted_2, _toDisplayString(_unref(t)('global.save')), 1)
  ], 40, _hoisted_1))
}
}

})